import { ArrowLeft, ArrowRight, Check, Paperclip, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { MAX_AMOUNT_ATTACHMENTS } from "shared/utils/constants";

import { Attachment } from "./types";
import { useAttachments } from "./use-attachments";
import { convertBlobToBase64, formatFileSize } from "./utils";
import { useSendGtmEvents } from "~/components/app-shell/providers/gtm-events-provider/gtm-events-provider";
import { useToasts } from "~/components/app-shell/providers/toasts-provider/toasts-provider";
import {
  BottomDrawerRef,
  BottomDrawerResponsive,
} from "~/components/bottom-drawer/responsive";
import { Text } from "~/components/text";
import { useT } from "~/i18n/use-t";
import { cn } from "~/util/cn";
import { useWindowDimensions } from "~/util/hooks";

export const AttachmentsButton: React.FC<{
  attachments: ReturnType<typeof useAttachments>;
  className?: string;
}> = ({ attachments, className }) => {
  const t = useT();
  const toasts = useToasts();
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const { width } = useWindowDimensions();
  const modalRef = useRef<BottomDrawerRef>(null);
  const [uploadedImages, setUploadedImages] = useState<Attachment[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { sendAttachmentClicked } = useSendGtmEvents();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    let list = Array.from(files);

    const allowedExtensions = /\.(jpg|jpeg|png)$/i;
    list = list.filter((file) => allowedExtensions.test(file.name));

    if (list.length === 0) {
      if (fileUploadRef.current) {
        fileUploadRef.current.value = "";
      }

      toasts.addToast({
        component: (
          <Text
            tx="chat.errors.invalidFileType"
            txData={{ extensions: ".jpg, .jpeg, .png" }}
          />
        ),
        variant: "error",
      });
      return;
    }

    const totalAttachments = attachments.list.length + list.length;
    if (totalAttachments > MAX_AMOUNT_ATTACHMENTS) {
      if (fileUploadRef.current) {
        fileUploadRef.current.value = "";
      }

      toasts.addToast({
        component: <Text tx="chat.errors.maximumFiveImages" />,
        variant: "error",
      });
      list = list.slice(0, MAX_AMOUNT_ATTACHMENTS - attachments.list.length);
    }

    try {
      const base64s = await Promise.all(
        list.map((file) => convertBlobToBase64(file)),
      );

      const newFiles: Attachment[] = list.map((file, index) => ({
        id: Math.random().toString(36).substring(2),
        file,
        preview: URL.createObjectURL(file),
        base64: base64s[index],
        name: file.name,
        size: file.size,
      }));

      const imageFiles = newFiles.filter((file) =>
        file.file.type.startsWith("image/"),
      );

      if (imageFiles.length > 0) {
        setUploadedImages(imageFiles);
        setCurrentImageIndex(0);
        modalRef.current?.open();
      } else {
        attachments.add(...newFiles);
      }
    } catch (err) {
      if (fileUploadRef.current) {
        fileUploadRef.current.value = "";
      }

      toasts.addToast({
        component: (
          <Text
            tx="cognito.customFieldsError"
            txData={{ error: "could not upload attachments" }}
          />
        ),
      });
    }

    if (fileUploadRef.current) {
      fileUploadRef.current.value = "";
    }
  };

  const triggerFileInput = (e: React.MouseEvent) => {
    if (width < 640) {
      e.preventDefault();

      const id = toasts.addToast({
        autoClose: false,
        component: (
          <div className="flex flex-col items-center gap-3">
            <Text tx="chat.onlyJpgPngAllowed" />
            <button
              title="upload"
              type="button"
              className="shadow-btn btn-primary btn-block btn"
              onClick={() => {
                if (fileUploadRef.current) {
                  fileUploadRef.current.click();
                }
                toasts.removeToast(id);
              }}
            >
              <Upload className="mr-2" />
              <Text tx="socialMedia.uploadMedia" />
            </button>
          </div>
        ),
      });
    } else {
      if (fileUploadRef.current) {
        fileUploadRef.current.click();
      }
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex >= uploadedImages.length - 1) {
      attachments.add(...uploadedImages);
      modalRef.current?.close();
      setUploadedImages([]);
      setCurrentImageIndex(0);
      return;
    }

    setCurrentImageIndex((prev) => prev + 1);
  };

  const handlePrevImage = () => {
    if (currentImageIndex <= 0) return;
    setCurrentImageIndex((prev) => prev - 1);
  };

  const isMaxAttachmentsReached =
    attachments.list.length >= MAX_AMOUNT_ATTACHMENTS;
  const tooltipText = isMaxAttachmentsReached
    ? t({ tx: "chat.errors.maximumFiveImages" })
    : t({ tx: "chat.onlyJpgPngAllowed" });

  const currentImage = uploadedImages[currentImageIndex];
  const isLastImage = currentImageIndex === uploadedImages.length - 1;

  return (
    <div
      className={cn("sm:tooltip sm:tooltip-right", className)}
      data-tip={tooltipText}
    >
      <button
        type="button"
        className="shadow-btn btn-sm btn h-10 w-12 border border-neutral-200 bg-neutral-100"
        onClick={(event) => {
          sendAttachmentClicked();
          triggerFileInput(event);
        }}
        disabled={isMaxAttachmentsReached}
        aria-label="Upload attachment"
      >
        <Paperclip width={20} className="text-neutral" />
      </button>
      <input
        title="upload"
        ref={fileUploadRef}
        id="file-upload"
        type="file"
        accept=".jpg, .jpeg, .png"
        onChange={handleFileUpload}
        className="hidden"
        multiple
        disabled={isMaxAttachmentsReached}
      />
      <BottomDrawerResponsive ref={modalRef}>
        {uploadedImages.length > 0 && currentImage && (
          <div className="flex flex-col">
            <div className="flex flex-nowrap items-center justify-between gap-2">
              <h3 className="mb-4 text-lg font-medium">
                <Text tx="attachments.confirmTitle" />
              </h3>
              {uploadedImages.length > 1 ? (
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      title="left"
                      className="shadow-btn btn-ghost btn-sm btn-square btn"
                      disabled={currentImageIndex === 0}
                      onClick={handlePrevImage}
                    >
                      <ArrowLeft size={16} />
                    </button>
                    <span className="w-7 text-sm">
                      {currentImageIndex + 1}
                      {"/"}
                      {uploadedImages.length}
                    </span>
                    <button
                      title="next"
                      className="shadow-btn btn-ghost btn-sm btn-square btn"
                      onClick={handleNextImage}
                      disabled={isLastImage}
                    >
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="border-neutral/20 relative h-64 w-full rounded-lg border bg-gradient-to-b from-base-200 to-base-300 p-4 shadow-lg">
              <img
                src={currentImage.preview}
                alt={currentImage.name}
                className="h-full w-full object-contain"
              />
            </div>

            <div className="mt-2 text-sm text-gray-600">
              {currentImage.name}
              {" ("}
              {formatFileSize(currentImage.size)}
              {")"}
            </div>

            <div className="mt-4 flex justify-between">
              <button
                title="close"
                className="shadow-btn btn-ghost btn"
                onClick={() => {
                  modalRef.current?.close();
                  setUploadedImages([]);
                }}
              >
                <Text tx="attachments.cancel" />
              </button>
              <button className="btn-primary btn" onClick={handleNextImage}>
                {isLastImage ? (
                  <>
                    <Check width={16} />
                    <Text tx="attachments.confirm" />
                  </>
                ) : (
                  <>
                    <ArrowRight width={16} />
                    <Text tx="attachments.next" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </BottomDrawerResponsive>
    </div>
  );
};
